// MCHD Room Scheduler - Planner Data Hooks
// React Query hooks for planner API integration with comprehensive caching and error handling
// Modified: 08-10-2025 16:53:00 - Enabled window focus refetch for real-time updates

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { API_CONFIG } from '../../api';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface PlannerGridFilters {
  startDate: string;
  endDate: string;
  buildingId?: string;
  floorId?: string;
  roomIds?: string[];
  sessionTemplateIds?: string[];
  userIds?: string[];
  statuses?: string[];
  includeBlocks?: boolean;
  includeConflicts?: boolean;
  includeUtilization?: boolean;
}

interface PlannerFilters {
  buildingId?: string;
  floorId?: string;
  startDate?: string;
  endDate?: string;
}

interface RoomUtilizationFilters {
  roomId?: string;
  buildingId?: string;
  floorId?: string;
  startDate: string;
  endDate: string;
  groupBy?: 'day' | 'week' | 'month';
}

interface FloorPlanFilters {
  floorId: string;
  date?: string;
  includeRooms?: boolean;
  includeAssignments?: boolean;
  includeBlocks?: boolean;
  includeSessionTimes?: boolean;
}

// =============================================================================
// API CLIENT FUNCTIONS
// =============================================================================

/**
 * Fetch planner grid data with comprehensive filtering
 */
async function fetchPlannerGridData(filters: PlannerGridFilters) {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach(item => params.append(key, item));
      } else {
        params.append(key, value.toString());
      }
    }
  });
  
  const response = await fetch(`${API_CONFIG.BASE_URL}/planner/grid?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    }
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch planner grid data: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Fetch planner filters data
 */
async function fetchPlannerFilters(filters: PlannerFilters = {}) {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, value.toString());
    }
  });
  
  const response = await fetch(`${API_CONFIG.BASE_URL}/planner/filters?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    }
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch planner filters: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Fetch room utilization data
 */
async function fetchRoomUtilization(filters: RoomUtilizationFilters) {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, value.toString());
    }
  });
  
  const response = await fetch(`${API_CONFIG.BASE_URL}/planner/utilization?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    }
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch room utilization: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Fetch floor plan data for Konva visualization
 */
async function fetchFloorPlanData(filters: FloorPlanFilters) {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, value.toString());
    }
  });
  
  const response = await fetch(`${API_CONFIG.BASE_URL}/planner/floor-plan/${filters.floorId}?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    }
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch floor plan data: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Fetch planner dashboard summary
 */
async function fetchPlannerDashboardSummary(filters: PlannerFilters = {}) {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, value.toString());
    }
  });
  
  const response = await fetch(`${API_CONFIG.BASE_URL}/planner/dashboard?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    }
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch planner dashboard: ${response.statusText}`);
  }
  
  return response.json();
}

/**
 * Export planner data
 */
async function exportPlannerData(format: 'json' | 'csv' | 'xlsx', filters: PlannerGridFilters) {
  const params = new URLSearchParams();
  params.append('format', format);
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach(item => params.append(key, item));
      } else {
        params.append(key, value.toString());
      }
    }
  });
  
  const response = await fetch(`${API_CONFIG.BASE_URL}/planner/export?${params}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    }
  });
  
  if (!response.ok) {
    throw new Error(`Failed to export planner data: ${response.statusText}`);
  }
  
  // Handle file download
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `planner-data.${format}`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
  
  return { success: true };
}

// =============================================================================
// REACT QUERY HOOKS
// =============================================================================

/**
 * Hook for fetching planner grid data
 */
export function usePlannerGridData(filters: PlannerGridFilters) {
  return useQuery({
    queryKey: ['plannerGrid', filters],
    queryFn: () => fetchPlannerGridData(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: true, // CRITICAL FIX: Auto-refresh when switching back to planner tab
  });
}

/**
 * Hook for fetching planner filters data
 */
export function usePlannerFilters(filters: PlannerFilters = {}) {
  return useQuery({
    queryKey: ['plannerFilters', filters],
    queryFn: () => fetchPlannerFilters(filters),
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    refetchOnWindowFocus: true, // CRITICAL FIX: Auto-refresh filter options when switching tabs
  });
}

/**
 * Hook for fetching room utilization data
 */
export function useRoomUtilization(filters: RoomUtilizationFilters) {
  return useQuery({
    queryKey: ['roomUtilization', filters],
    queryFn: () => fetchRoomUtilization(filters),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for fetching floor plan data
 */
export function useFloorPlanData(filters: FloorPlanFilters) {
  return useQuery({
    queryKey: ['floorPlan', filters],
    queryFn: () => fetchFloorPlanData(filters),
    staleTime: 30 * 1000, // Reduced to 30 seconds for fresher data
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: true, // Auto-refresh when switching tabs
    refetchOnMount: 'always', // Always refetch when component mounts
    enabled: !!filters.floorId,
  });
}

/**
 * Hook for fetching planner dashboard summary
 */
export function usePlannerDashboardSummary(filters: PlannerFilters = {}) {
  return useQuery({
    queryKey: ['plannerDashboard', filters],
    queryFn: () => fetchPlannerDashboardSummary(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: true, // CRITICAL FIX: Auto-refresh dashboard when switching tabs
  });
}

/**
 * Hook for exporting planner data
 */
export function useExportPlannerData() {
  return useMutation({
    mutationFn: ({ format, filters }: { format: 'json' | 'csv' | 'xlsx'; filters: PlannerGridFilters }) =>
      exportPlannerData(format, filters),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Export completed successfully',
        color: 'green',
        autoClose: 3000
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Export Failed',
        message: error.message,
        color: 'red',
        autoClose: 5000
      });
    }
  });
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook for invalidating planner queries
 */
export function useInvalidatePlannerQueries() {
  const queryClient = useQueryClient();
  
  return {
    invalidateGrid: () => queryClient.invalidateQueries({ queryKey: ['plannerGrid'] }),
    invalidateFilters: () => queryClient.invalidateQueries({ queryKey: ['plannerFilters'] }),
    invalidateUtilization: () => queryClient.invalidateQueries({ queryKey: ['roomUtilization'] }),
    invalidateConflicts: () => queryClient.invalidateQueries({ queryKey: ['conflictAnalysis'] }),
    invalidateFloorPlan: () => queryClient.invalidateQueries({ queryKey: ['floorPlan'] }),
    invalidateDashboard: () => queryClient.invalidateQueries({ queryKey: ['plannerDashboard'] }),
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: ['plannerGrid'] });
      queryClient.invalidateQueries({ queryKey: ['plannerFilters'] });
      queryClient.invalidateQueries({ queryKey: ['roomUtilization'] });
      queryClient.invalidateQueries({ queryKey: ['conflictAnalysis'] });
      queryClient.invalidateQueries({ queryKey: ['floorPlan'] });
      queryClient.invalidateQueries({ queryKey: ['plannerDashboard'] });
    }
  };
}

/**
 * Hook for prefetching planner data
 */
export function usePrefetchPlannerData() {
  const queryClient = useQueryClient();
  
  return {
    prefetchGrid: (filters: PlannerGridFilters) =>
      queryClient.prefetchQuery({
        queryKey: ['plannerGrid', filters],
        queryFn: () => fetchPlannerGridData(filters),
        staleTime: 5 * 60 * 1000
      }),
    prefetchFloorPlan: (filters: FloorPlanFilters) =>
      queryClient.prefetchQuery({
        queryKey: ['floorPlan', filters],
        queryFn: () => fetchFloorPlanData(filters),
        staleTime: 5 * 60 * 1000
      })
  };
}
